import { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { authApi, useAuthStore } from '../model/auth.store.js';
import type { TelegramStartDto } from '../api/auth.api.js';
import { TelegramWaitPanel } from './TelegramWaitPanel.js';

/**
 * TELEGRAM orqali kirish.
 *
 * O'z tarmoq oqimiga ega alohida birlik va bu ataylab: `LoginPage` — sof
 * ko'rinish komponenti (tarmoqni bilmaydi, alohida sinaladi). Telegram oqimi
 * esa so'rov ochish, havolani ochish va javobni kutish — uchalasi ham holat
 * talab qiladi.
 *
 * CAPTCHA bu yerda TALAB QILINMAYDI va bu ataylab: Telegram — AYRIM yo'l.
 * Rasm parolni terib ko'rishni qimmatlashtirish uchun, Telegramda esa
 * teriladigan parol yo'q. Bog'lansa, rasmni yechmaguncha Telegram tugmasi
 * ham o'chiq turardi va ikkita mustaqil yo'l bir-biriga tugilib qolardi.
 */

/**
 * Javobni so'rash oralig'i.
 *
 * 2 soniya — odam uchun sezilarli kechikish emas, 5 daqiqada esa ~150 ta
 * so'rov: bu qidiruv emas, bitta indekslangan qator bo'yicha o'qish.
 */
const POLL_MS = 2000;

export const TelegramLoginButton = () => {
  const loginWithTokens = useAuthStore((s) => s.loginWithTokens);
  const [started, setStarted] = useState<TelegramStartDto | null>(null);
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const request = useRef<TelegramStartDto | null>(null);

  // Sahifadan chiqilganda so'rash to'xtashi SHART, aks holda yopilgan
  // komponent holatini yangilashga urinib, xatolar oqimi boshlanardi.
  const alive = useRef(true);
  useEffect(() => {
    /*
     * Bayroq har o'rnatishda QAYTA yoqiladi.
     *
     * `useEffect(() => () => { alive.current = false }, [])` yozilgan edi va
     * u ishlamadi: React dasturchi rejimida effektni ikki marta chaqiradi
     * (o'rnatish → tozalash → o'rnatish), tozalash esa bayroqni butunlay
     * o'chirib qo'yardi. Natijada javob kelgani bilan holat yangilanmasdi va
     * rasm mangu "yuklanmoqda" holatida qolardi.
     */
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const stop = (text: string | null): void => {
    if (!alive.current) return;
    request.current = null;
    setStarted(null);
    setMessage(text);
  };

  const poll = async (): Promise<void> => {
    const current = request.current;
    if (current === null || !alive.current) return;

    try {
      const result = await authApi.telegramStatus(current.nonce, current.pollSecret);
      if (!alive.current) return;

      if (result.state === 'APPROVED' && result.tokens !== undefined) {
        // Yo'naltirishni `LoginRoute` bajaradi: u tokendagi o'zgarishni
        // kuzatib turadi va bu komponent marshrutlashni bilmaydi.
        await loginWithTokens(result.tokens);
        return;
      }
      if (result.state === 'REJECTED') return stop(result.reason ?? t('login.telegramRejected'));
      if (result.state === 'EXPIRED') return stop(t('login.telegramExpired'));

      setTimeout(() => void poll(), POLL_MS);
    } catch {
      stop(t('login.telegramFailed'));
    }
  };

  const begin = (): void => {
    setMessage(null);
    setStarting(true);

    /*
     * Oyna BOSISH PAYTIDA ochiladi, so'rov javobidan keyin emas.
     *
     * Brauzer `window.open` ni faqat foydalanuvchi harakatining bevosita
     * davomida o'tkazadi. `await` dan keyin chaqirilsa, u harakatdan
     * "uzilib" qoladi va qalqib chiquvchi oyna sifatida jimgina to'siladi —
     * tugma bosilgani bilan hech nima ochilmasdi. Shuning uchun avval bo'sh
     * oyna olinadi, havola kelgach unga yo'naltiriladi.
     *
     * `noopener` OYNA XOSSASI sifatida BERILMAYDI va bu ataylab: shunda
     * `window.open` spetsifikatsiya bo'yicha `null` qaytaradi va ochilgan
     * oynaga yo'naltirish uchun tutqich qolmaydi — oyna bo'sh ochilib,
     * hech qayerga bormasdi. Aynan shu xato bo'lgan edi.
     *
     * Bog'lanish o'rniga QO'LDA uziladi: `opener = null`. Shu paytda oyna
     * hali `about:blank`, ya'ni bir xil manbada va unga yozish mumkin.
     * Natija bir xil — t.me sahifasi bu oynani boshqara olmaydi.
     */
    const popup = window.open('about:blank', '_blank');
    if (popup !== null) popup.opener = null;

    void authApi
      .telegramStart()
      .then((response) => {
        if (!alive.current) {
          popup?.close();
          return;
        }
        request.current = response;
        setStarted(response);

        // Oyna to'silgan bo'lsa (`null`) muammo yo'q: panelda "Botni ochish"
        // havolasi turadi va uni odam o'zi bosadi.
        if (popup !== null) popup.location.replace(response.deepLink);
        setTimeout(() => void poll(), POLL_MS);
      })
      .catch((cause: unknown) => {
        popup?.close();
        stop(cause instanceof Error ? cause.message : t('login.telegramFailed'));
      })
      .finally(() => {
        if (alive.current) setStarting(false);
      });
  };

  if (started !== null) {
    return (
      <TelegramWaitPanel
        deepLink={started.deepLink}
        matchCode={started.matchCode}
        expiresAt={started.expiresAt}
        onCancel={() => stop(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="w-full"
        loading={starting}
        onClick={begin}
      >
        <Send size={16} aria-hidden />
        {t('login.telegramButton')}
      </Button>

      {message === null ? null : (
        <p
          role="alert"
          className={cn(
            'border-warn/30 bg-warn-soft text-warn rounded-[var(--radius-sm)]',
            'border px-3 py-2 text-sm text-pretty',
          )}
        >
          {message}
        </p>
      )}
    </div>
  );
};
